import React, { useState, useEffect, useRef } from 'react';
import { firestore } from '../firebase/config';
import { collection, addDoc, onSnapshot, query, orderBy, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useToast } from './ToastContext';

const ChatComponent = ({ userRole, userId, userName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatMode, setChatMode] = useState('all'); // 'all' or 'admin'
  const [previousMessagesCount, setPreviousMessagesCount] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null); // Track message being replied to
  const messagesEndRef = useRef(null);
  const { showSuccess, showError } = useToast();

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Periodic cleanup of old messages (every hour)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      if (messages.length > 0) {
        deleteOldMessages(messages);
      }
    }, 60 * 60 * 1000); // Run every hour

    return () => clearInterval(cleanupInterval);
  }, [messages]);

  // Function to send browser notification
  const sendNotification = (title, body, icon = null) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: icon || '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'chat-notification',
        requireInteraction: false,
        silent: false
      });
    }
  };

  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      // Create a simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio notification not supported');
    }
  };

  // Function to check if message is older than 48 hours
  const isMessageOlderThan48Hours = (timestamp) => {
    const messageTime = new Date(timestamp);
    const currentTime = new Date();
    const timeDifference = currentTime.getTime() - messageTime.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference > 48;
  };

  // Function to delete old messages from Firebase
  const deleteOldMessages = async (messages) => {
    const oldMessages = messages.filter(msg => isMessageOlderThan48Hours(msg.timestamp));
    
    if (oldMessages.length > 0) {
      try {
        const deletePromises = oldMessages.map(msg => 
          deleteDoc(doc(firestore, 'messages', msg.id))
        );
        await Promise.all(deletePromises);
        console.log(`Deleted ${oldMessages.length} old messages`);
      } catch (error) {
        console.error('Error deleting old messages:', error);
      }
    }
  };

  // Listen for messages sent to this user
  useEffect(() => {
    if (!userId) return;

    const messagesQuery = query(
      collection(firestore, 'messages'),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const allMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

             // Filter messages relevant to this user and exclude old messages
       const relevantMessages = allMessages.filter(msg => {
         // Exclude messages older than 48 hours
         if (isMessageOlderThan48Hours(msg.timestamp)) {
           return false;
         }
         
         // Messages sent by admin to all users
         if (msg.senderType === 'admin' && msg.isBroadcast) {
           if (msg.recipientType === 'all') return true;
           if (msg.recipientType === userRole + 's') return true; // 'students', 'employees', 'interns', 'managers'
         }
         
         // Messages sent specifically to this user
         if (msg.senderType === 'admin' && msg.recipientId === userId) return true;
         
         // Messages sent by this user
         if (msg.senderType === userRole && msg.senderId === userId) return true;
         
         // For students: show all student messages in 'all' mode (but not admin-specific messages)
         if (userRole === 'student' && chatMode === 'all' && msg.senderType === 'student' && !msg.recipientId) {
           return true;
         }
         
         // For employees and interns: show messages from same user type AND student messages in 'all' mode
         if ((userRole === 'employee' || userRole === 'intern') && chatMode === 'all' && !msg.recipientId) {
           if (msg.senderType === userRole || msg.senderType === 'student') {
             return true;
           }
         }
         
         // For students: also show employee and intern messages in 'all' mode
         if (userRole === 'student' && chatMode === 'all' && !msg.recipientId) {
           if (msg.senderType === 'employee' || msg.senderType === 'intern') {
             return true;
           }
         }
         
         // For other user types: show messages from same user type in 'all' mode (but not admin-specific messages)
         if (chatMode === 'all' && msg.senderType === userRole && !msg.recipientId) {
           return true;
         }
         
         // Messages sent specifically to admin (only admin can see these)
         if (msg.senderType !== 'admin' && msg.recipientId === 'admin' && userRole === 'admin') {
           return true;
         }
         
         return false;
       });

       // Delete old messages from Firebase (run periodically)
       deleteOldMessages(allMessages);

      // Check for new messages and send notifications
      if (relevantMessages.length > previousMessagesCount && previousMessagesCount > 0) {
        const newMessages = relevantMessages.slice(previousMessagesCount);
        newMessages.forEach(message => {
          // Only notify for messages from other users
          if (message.senderId !== userId && !message.read) {
            const senderName = message.senderName || 
              (message.senderType === 'admin' ? 'Admin' : 
               message.senderType === 'student' ? 'Student' :
               message.senderType === 'employee' ? 'Employee' :
               message.senderType === 'intern' ? 'Intern' :
               message.senderType === 'manager' ? 'Manager' : 'User');
            
            const notificationTitle = `New message from ${senderName}`;
            const notificationBody = message.text.length > 50 
              ? `${message.text.substring(0, 50)}...` 
              : message.text;
            
            // Send browser notification
            sendNotification(notificationTitle, notificationBody);
            
            // Play notification sound
            playNotificationSound();
            
            // Send toast notification if chat is not open
            if (!isOpen) {
              showSuccess(`New message from ${senderName}`);
            }
          }
        });
      }

      setMessages(relevantMessages);
      setPreviousMessagesCount(relevantMessages.length);
      
      // Count unread messages (messages from admin and other students that this user hasn't read)
      const unread = relevantMessages.filter(msg => 
        !msg.read && msg.senderId !== userId
      ).length;
      setUnreadCount(unread);
    });

         return () => unsubscribe();
   }, [userId, userRole, chatMode]);

  // Auto scroll to bottom when new messages arrive or when chat is opened
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, isOpen]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !userId) return;

    setLoading(true);
    try {
      const messageData = {
        text: newMessage.trim(),
        senderType: userRole,
        senderId: userId,
        senderName: userName || 'User',
        timestamp: new Date().toISOString(),
        read: false,
        recipientType: chatMode === 'admin' ? 'admin' : 'all',
        recipientId: chatMode === 'admin' ? 'admin' : null,
        isBroadcast: chatMode === 'all',
        replyTo: replyingTo ? {
          messageId: replyingTo.id,
          text: replyingTo.text,
          senderName: replyingTo.senderName,
          senderType: replyingTo.senderType
        } : null
      };

      await addDoc(collection(firestore, 'messages'), messageData);
      setNewMessage('');
      setReplyingTo(null); // Clear reply state
      showSuccess('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to send message');
    }
    setLoading(false);
  };

  const markAsRead = async (messageId) => {
    try {
      await updateDoc(doc(firestore, 'messages', messageId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleMessageDoubleClick = (message) => {
    setReplyingTo(message);
    // Focus on the message input
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder*="Type your message"]');
      if (textarea) {
        textarea.focus();
      }
    }, 100);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

     const getMessageSender = (message) => {
     if (message.senderType === 'admin') {
       return 'Admin';
     }
     if (message.senderId === userId) {
       return 'You';
     }
     return message.senderName || `Unknown ${message.senderType === 'student' ? 'Student' : message.senderType === 'employee' ? 'Employee' : message.senderType === 'intern' ? 'Intern' : message.senderType === 'manager' ? 'Manager' : 'User'}`;
   };

  const isFromAdmin = (message) => {
    return message.senderType === 'admin' && message.senderId !== userId;
  };

  const isFromOtherUser = (message) => {
    return message.senderId !== userId;
  };

  // Mark all unread messages as read when chat is opened
  const markAllAsRead = async () => {
    const unreadMessages = messages.filter(msg => !msg.read && msg.senderId !== userId);
    const updatePromises = unreadMessages.map(msg => 
      updateDoc(doc(firestore, 'messages', msg.id), { read: true })
    );
    
    try {
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Mark all messages as read when chat is opened and scroll to first unread message
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      markAllAsRead();
      
      // Find first unread message and scroll to it
      const firstUnreadIndex = messages.findIndex(msg => !msg.read && msg.senderId !== userId);
      if (firstUnreadIndex !== -1) {
        setTimeout(() => {
          const messageElements = document.querySelectorAll('[data-message-id]');
          if (messageElements[firstUnreadIndex]) {
            messageElements[firstUnreadIndex].scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }
        }, 200);
      }
    }
  }, [isOpen, messages, unreadCount]);

  return (
    <>
             {/* Fixed Chat Icon */}
       <div className="fixed bottom-6 right-6 z-50">
         <button
           onClick={() => setIsOpen(!isOpen)}
           className="relative bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-600 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-4 focus:ring-emerald-300 backdrop-blur-sm"
           title="Chat with Admin - Send messages to admin"
         >
           <div className="absolute inset-0 bg-white/10 rounded-full animate-pulse"></div>
           <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
           </svg>
           {unreadCount > 0 && !isOpen && (
             <span className="absolute -top-2 -right-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold animate-bounce shadow-lg">
               {unreadCount > 9 ? '9+' : unreadCount}
             </span>
           )}
         </button>
       </div>

             {/* Chat Modal */}
       {isOpen && (
         <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center animate-in slide-in-from-bottom-4 duration-300">
                       <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-2xl w-[90%] h-full flex flex-col border border-gray-200/50">
                         {/* Header */}
             <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white p-6 relative overflow-hidden">
               <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
               <div className="relative z-10 flex items-center justify-between">
                 <div className="flex items-center space-x-4">
                   <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30">
                     <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                     </svg>
                   </div>
                   <div>
                     <h3 className="font-bold text-xl flex items-center space-x-3">
                       <span>{userRole === 'student' ? 'Student Chat' : userRole === 'employee' ? 'Employee Chat' : userRole === 'intern' ? 'Intern Chat' : userRole === 'manager' ? 'Manager Chat' : 'Chat with Admin'}</span>
                       {isOpen && unreadCount > 0 && (
                         <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold animate-pulse shadow-lg">
                           {unreadCount > 9 ? '9+' : unreadCount}
                         </span>
                       )}
                     </h3>
                                            <p className="text-emerald-100 text-sm font-medium">
                         {chatMode === 'all' 
                           ? userRole === 'student' 
                             ? 'Chat with all students, employees & interns'
                             : userRole === 'employee'
                             ? 'Chat with all employees, students & interns'
                             : userRole === 'intern'
                             ? 'Chat with all interns, students & employees'
                             : `Chat with all ${userRole === 'manager' ? 'managers' : userRole + 's'}`
                           : 'Send messages to admin'
                         }
                       </p>
                   </div>
                 </div>
                 <button
                   onClick={() => setIsOpen(false)}
                   className="text-white hover:text-emerald-100 transition-all duration-200 hover:scale-110 p-2 rounded-full hover:bg-white/10"
                 >
                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
               </div>
             </div>

                         {/* Message Input Section */}
             <div className="p-6 border-b border-gray-200/50 bg-gradient-to-r from-gray-50 to-white">
               {/* Chat Mode Toggle for All Users */}
               <div className="mb-4 flex space-x-3">
                 <button
                   onClick={() => setChatMode('all')}
                   className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                     chatMode === 'all'
                       ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg transform scale-105'
                       : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                   }`}
                 >
                   👥 {userRole === 'student' ? 'All Students, Employees & Interns' : userRole === 'employee' ? 'All Employees, Students & Interns' : userRole === 'intern' ? 'All Interns, Students & Employees' : userRole === 'manager' ? 'All Managers' : 'All ' + userRole + 's'}
                 </button>
                 <button
                   onClick={() => setChatMode('admin')}
                   className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                     chatMode === 'admin'
                       ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg transform scale-105'
                       : 'bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-50 border border-gray-200'
                   }`}
                 >
                   👨‍💼 Admin Only
                 </button>
               </div>

               {/* Reply Preview */}
               {replyingTo && (
                 <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl relative">
                   <div className="flex items-center justify-between mb-2">
                     <span className="text-xs font-semibold text-blue-600 flex items-center">
                       <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                       </svg>
                       Replying to {getMessageSender(replyingTo)}
                     </span>
                     <button
                       onClick={cancelReply}
                       className="text-blue-500 hover:text-blue-700 transition-colors"
                     >
                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                       </svg>
                     </button>
                   </div>
                   <div className="text-sm text-gray-700 bg-white/50 rounded-lg p-2 border-l-4 border-blue-400">
                     {replyingTo.text.length > 100 
                       ? `${replyingTo.text.substring(0, 100)}...` 
                       : replyingTo.text
                     }
                   </div>
                 </div>
               )}
               
               <div className="flex space-x-3">
                                                   <textarea
                   value={newMessage}
                   onChange={(e) => setNewMessage(e.target.value)}
                   onKeyPress={handleKeyPress}
                                        placeholder={
                       chatMode === 'all'
                         ? userRole === 'student'
                           ? '💬 Type your message to all students, employees & interns...'
                           : userRole === 'employee'
                           ? '💬 Type your message to all employees, students & interns...'
                           : userRole === 'intern'
                           ? '💬 Type your message to all interns, students & employees...'
                           : `💬 Type your message to all ${userRole === 'manager' ? 'managers' : userRole + 's'}...`
                         : "💬 Type your message to admin..."
                     }
                   className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none min-h-[60px] max-h-[120px] bg-white shadow-sm transition-all duration-200"
                   rows="2"
                 />
                 <button
                   onClick={sendMessage}
                   disabled={loading || !newMessage.trim()}
                   className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                 >
                  {loading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

                         {/* Messages */}
             <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
                             {messages.length === 0 ? (
                 <div className="text-center text-gray-500 py-12">
                   <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
                     <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                     </svg>
                   </div>
                   <h3 className="text-lg font-semibold text-gray-700 mb-2">No messages yet</h3>
                   <p className="text-sm text-gray-500">Start a conversation with admin!</p>
                 </div>
               ) : (
                messages.map((message) => (
                                     <div
                     key={message.id}
                     data-message-id={message.id}
                     className={`flex ${message.senderType === userRole ? 'justify-end' : 'justify-start'} animate-in fade-in duration-300 cursor-pointer hover:opacity-80 transition-opacity`}
                     onClick={() => !message.read && isFromOtherUser(message) && markAsRead(message.id)}
                     onDoubleClick={() => handleMessageDoubleClick(message)}
                     title="Double-click to reply"
                   >
                     <div
                       className={`w-[60%] px-4 py-3 rounded-2xl shadow-lg ${
                         message.senderType === userRole
                           ? 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-emerald-500/25'
                           : message.senderType === 'admin' && message.recipientId === userId
                           ? 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-gray-800 border-2 border-blue-300 shadow-blue-500/25'
                           : 'bg-gradient-to-br from-white via-gray-50 to-gray-100 text-gray-800 border border-gray-200/50 shadow-gray-500/10'
                       } ${!message.read && isFromOtherUser(message) ? 'ring-2 ring-emerald-300 shadow-xl' : ''} ${message.senderType === 'admin' && message.recipientId === userId ? 'ring-2 ring-blue-400 shadow-blue-500/50' : ''} backdrop-blur-sm relative`}
                     >
                       {/* Reply Preview */}
                       {message.replyTo && (
                         <div className="mb-3 p-2 bg-black/10 rounded-lg border-l-4 border-white/30">
                           <div className="text-xs opacity-80 mb-1 flex items-center">
                             <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                             </svg>
                             {getMessageSender({ senderType: message.replyTo.senderType, senderName: message.replyTo.senderName })}
                           </div>
                           <div className="text-xs opacity-70">
                             {message.replyTo.text.length > 50 
                               ? `${message.replyTo.text.substring(0, 50)}...` 
                               : message.replyTo.text
                             }
                           </div>
                         </div>
                       )}

                                             <div className="flex items-center space-x-2 mb-2">
                         <span className="text-xs font-semibold opacity-90 flex items-center">
                           <span className="w-2 h-2 rounded-full mr-2 ${
                             message.senderType === 'admin' ? 'bg-blue-400' :
                             message.senderType === 'student' ? 'bg-emerald-400' :
                             message.senderType === 'employee' ? 'bg-purple-400' :
                             message.senderType === 'intern' ? 'bg-orange-400' :
                             message.senderType === 'manager' ? 'bg-indigo-400' : 'bg-gray-400'
                           }"></span>
                           {getMessageSender(message)}
                         </span>
                         {isFromOtherUser(message) && !message.read && (
                           <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-sm"></span>
                         )}
                         {/* Special crown icon for admin-specific messages */}
                         {message.senderType === 'admin' && message.recipientId === userId && (
                           <span className="text-blue-500 text-lg animate-pulse">👑</span>
                         )}
                       </div>
                                             <div className="text-sm break-words whitespace-pre-wrap overflow-hidden word-wrap leading-relaxed font-medium">
                         {message.text}
                       </div>
                       <div className="text-xs opacity-70 mt-2 flex items-center space-x-1">
                         <span className="flex items-center">
                           <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                           </svg>
                           {formatTime(message.timestamp)}
                         </span>
                         {message.isBroadcast && (
                           <span className="text-xs mx-1">•</span>
                         )}
                       </div>
                                                                    {message.isBroadcast && message.senderType === 'admin' && (
                         <div className="text-xs opacity-80 mt-2 flex items-center space-x-1 bg-white/20 rounded-full px-2 py-1 w-fit">
                           <span>📢</span>
                           <span>Broadcast message</span>
                         </div>
                       )}
                       {message.isBroadcast && message.senderType === 'student' && (
                         <div className="text-xs opacity-80 mt-2 flex items-center space-x-1 bg-white/20 rounded-full px-2 py-1 w-fit">
                           <span>👥</span>
                           <span>To all students, employees & interns</span>
                         </div>
                       )}
                       {message.isBroadcast && message.senderType === 'employee' && (
                         <div className="text-xs opacity-80 mt-2 flex items-center space-x-1 bg-white/20 rounded-full px-2 py-1 w-fit">
                           <span>👥</span>
                           <span>To all employees, students & interns</span>
                         </div>
                       )}
                       {message.isBroadcast && message.senderType === 'intern' && (
                         <div className="text-xs opacity-80 mt-2 flex items-center space-x-1 bg-white/20 rounded-full px-2 py-1 w-fit">
                           <span>👥</span>
                           <span>To all interns, students & employees</span>
                         </div>
                       )}
                       {message.isBroadcast && message.senderType === 'manager' && (
                         <div className="text-xs opacity-80 mt-2 flex items-center space-x-1 bg-white/20 rounded-full px-2 py-1 w-fit">
                           <span>👥</span>
                           <span>To all managers</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent specifically to this user by admin */}
                       {message.senderType === 'admin' && message.recipientId === userId && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-blue-400 to-indigo-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>👑</span>
                           <span className="font-semibold">Direct Message from Admin</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent by student specifically to admin */}
                       {message.senderType === 'student' && message.recipientType === 'admin' && message.recipientId === 'admin' && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>🎯</span>
                           <span className="font-semibold">Student → Admin Only</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent by employee specifically to admin */}
                       {message.senderType === 'employee' && message.recipientType === 'admin' && message.recipientId === 'admin' && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-blue-400 to-indigo-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>🎯</span>
                           <span className="font-semibold">Employee → Admin Only</span>
                         </div>
                       )}
                       {/* Special indicator for messages sent by intern specifically to admin */}
                       {message.senderType === 'intern' && message.recipientType === 'admin' && message.recipientId === 'admin' && (
                         <div className="text-xs mt-2 flex items-center space-x-1 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full px-3 py-1 w-fit shadow-lg animate-pulse">
                           <span>🎯</span>
                           <span className="font-semibold">Intern → Admin Only</span>
                         </div>
                       )}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatComponent; 